// =====================================================================
// MI CUENTA · DIRECTRIZ-CUENTA-Y-ACTUALIZACION Parte A (A3)
// =====================================================================
// El panel de la persona, abierto desde su nombre en la cabecera. Siete
// secciones en el orden que decidió el dueño. Las REGLAS DE CAMBIO llegan
// del servidor como datos (`reglas`): la pantalla no decide quién puede
// qué, lo pinta. Y cada medio de recuperación trae su verdad: nada se
// pinta disponible porque «debería».
import { useCallback, useEffect, useRef, useState } from 'react';
import { UserRound, LifeBuoy, KeyRound, MonitorSmartphone, Palette, ShieldCheck, FileDown, Camera, Trash2, Upload, X, MailCheck, FileText, ChevronRight, Hand } from 'lucide-react';
import { api } from '../api';
import { prepararFoto, subirConProgreso, avisarCambioDeFoto } from './foto';
import { etiquetaRol } from '../lib/roles';
import { elegir } from '../lib/tema';
import { CampoValidado } from '../ui/Formulario';
import ModalConfirmar from '../ui/ModalConfirmar';
import CampoContrasena from '../ui/CampoContrasena';
import SeguridadDeLaCuenta from './SeguridadDeLaCuenta';
import Motivo from '../experiencia/Motivo';
import Ayuda from '../ui/Ayuda';
import { SesionesDelNavegador } from '../paginas/MisSesiones';
import { fechaCorta } from './plazoArco';
import { guardarArchivo } from '../ui/descarga';

const ETIQUETA_MEDIO = { ADMINISTRADOR: 'Quien administra tu empresa', CORREO: 'Correo personal', TELEGRAM: 'Telegram' };
const COMO = {
  sola: 'Lo cambias tú.',
  directo: 'Lo cambias directo; queda en la bitácora con el valor anterior.',
  solicitud: 'Se solicita: lo firma quien administra.',
  nunca: 'No se cambia desde aquí.',
};

function Seccion({ icono: Icono, titulo, descripcion, children }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Icono className="h-5 w-5 text-[var(--color-marca)] mt-0.5 shrink-0" />
        <div>
          <h2 className="font-semibold text-gray-900">{titulo}</h2>
          <p className="text-xs text-gray-500">{descripcion}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function Aviso({ tipo = 'ok', children }) {
  const clases = tipo === 'ok' ? 'text-green-800 bg-green-50 border-green-200'
    : tipo === 'error' ? 'text-red-700 bg-red-50 border-red-200' : 'text-amber-900 bg-amber-50 border-amber-200';
  return <p role={tipo === 'error' ? 'alert' : 'status'} className={`text-sm border rounded-lg p-2.5 ${clases}`}>{children}</p>;
}

export default function MiCuenta({ irA }) {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [version, setVersion] = useState(0);
  const recargar = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let vivo = true;
    api.get('/cuenta/mi')
      .then((d) => { if (vivo) { setDatos(d); setError(null); } })
      .catch((e) => { if (vivo) setError(e.message || 'No se pudo cargar tu cuenta. Revisa la conexión y vuelve a intentarlo.'); });
    return () => { vivo = false; };
  }, [version]);

  if (error) return <Aviso tipo="error">{error}</Aviso>;
  if (!datos) return <p className="text-sm text-gray-500">Cargando tu cuenta…</p>;

  const u = datos.usuario;
  const reglas = datos.reglas;
  return (
    <div className="space-y-4 max-w-3xl">
      <p className="text-sm text-gray-600">
        Lo tuyo en TEGO: cómo te ves, cómo recuperas tu cuenta y qué puedes hacer. Lo que no se cambia desde aquí dice por qué y quién puede.
      </p>
      <FotoYNombre u={u} reglas={reglas} pendientes={datos.solicitudes_pendientes} onCambio={recargar} />
      <CorreoDeAcceso u={u} regla={reglas.email} pendiente={datos.correo_acceso_pendiente} onCambio={recargar} />
      <Recuperacion u={u} medios={datos.medios} telegram={datos.telegram} onCambio={recargar} />
      <Seccion icono={KeyRound} titulo="Contraseña" descripcion="Solo tú la conoces. Cámbiala cuando quieras.">
        <button type="button" onClick={() => irA?.('__password')} className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
          Cambiar mi contraseña
        </button>
      </Seccion>
      {/* ESTABILIDAD Parte D: segundo factor, llaves de acceso y aparatos
          de confianza, junto a la contraseña, que es donde se buscan. */}
      <SeguridadDeLaCuenta />
      {/* TODO-SIRVE F2 (30 sep 2026): la lista, aquí mismo, con «Cerrar»
          por sesión. Antes decía «5 sesión(es)» sin enseñar cuáles. Es la
          MISMA lista que la pantalla completa (se importa, no se copia). */}
      <Seccion icono={MonitorSmartphone} titulo="Sesiones abiertas" descripcion="Dónde tienes TEGO abierto ahora mismo. Cierra la que no reconozcas.">
        <SesionesDelNavegador alCambiar={recargar} />
        <button type="button" onClick={() => irA?.('mis-sesiones')} className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
          Ver también las terminales donde trabajas
        </button>
      </Seccion>
      <Aspecto u={u} reglas={reglas} onCambio={recargar} />
      <Seccion icono={ShieldCheck} titulo="Mi rol y permisos" descripcion="Lo que puedes hacer hoy. Cambiarlo es de quien administra, desde Personal.">
        <p className="text-sm"><span className="font-medium">Rol:</span> {etiquetaRol(u.rol)} <span className="text-xs text-gray-500">— {reglas.rol.porque}</span></p>
        {/* CON NOMBRE, NO CON CLAVE (EXPERIENCIA E3, parche del integrador en el
            merge): `pago_a_proveedor` es un nombre de tabla a ojos de una
            persona. Los nombres vienen de /api/experiencia/mis-permisos;
            si no llegan, se enseñan las claves como antes. */}
        {/* TODO-SIRVE F2-B: agrupados por módulo, con plegado, y «Pedir un
            permiso». Si la ruta nueva no contesta (un servidor anterior),
            se enseña la lista de antes: nada se queda en blanco. */}
        <MisPermisos codigos={datos.permisos} />
      </Seccion>
      <MisDatos />
      {/* EL SELLO DE LO QUE SE ESTÁ MIRANDO (TODO-SIRVE Fase 4, 19 sep 2026):
          la versión que trae el paquete y el nombre del paquete mismo, para
          que el dueño y el soporte sepan qué carga el navegador sin abrir su
          consola. Si lo que ve no coincide con lo desplegado, la causa es la
          caché, no el servidor. */}
      <p className="text-xs text-gray-400 pt-2">
        TEGO {typeof __TEGO_VERSION__ !== 'undefined' ? __TEGO_VERSION__ : 'desarrollo'} · paquete {nombreDelPaquete()}
      </p>
    </div>
  );
}

// El nombre del archivo JS que está corriendo (`index-CrFcyCD8`): Vite le
// pone el hash del contenido, así que identifica la compilación exacta.
function nombreDelPaquete() {
  try {
    const ruta = new URL(import.meta.url).pathname;
    const m = ruta.match(/(index-[A-Za-z0-9_-]+)\.js$/);
    return m ? m[1] : 'desarrollo';
  } catch { return 'desarrollo'; }
}

// =====================================================================
// LA FOTO · rehecha el 19 de septiembre de 2026 tras el reporte de la
// primera clienta («Seleccionar archivo no hace nada»).
// =====================================================================
// LO QUE PASABA, MEDIDO: el servidor guardaba y servía bien (201 y
// image/webp), pero el control era el `<input type="file">` desnudo del
// navegador —«Ningún archivo seleccionado» aunque se hubiera subido,
// porque se vaciaba al terminar—, sin vista previa, sin botón de subir,
// y con el mensaje de éxito o de rechazo pintado DEBAJO del bloque del
// nombre, fuera de la pantalla de un teléfono. Una foto de cámara pasa
// de 2 MB, así que el caso más común era: elegir, no ver nada, y el
// rechazo diez líneas más abajo.
//
// AHORA: botón grande «Elegir foto», vista previa, «Subir foto» con
// barra de avance, «Quitar foto», y todo mensaje pegado al control. La
// foto se reduce en el aparato antes de salir (`foto.js`), y la cabecera
// cambia sin recargar (evento que escucha `auth.jsx`).
function Foto({ u, onCambio }) {
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [sello, setSello] = useState(Date.now());
  const [tieneFoto, setTieneFoto] = useState(!!u.tiene_foto);
  const [elegida, setElegida] = useState(null);      // { blob, url, reducida }
  const [progreso, setProgreso] = useState(null);    // null = quieta · 0..100 subiendo
  const [quitando, setQuitando] = useState(false);
  const archivoRef = useRef(null);

  useEffect(() => setTieneFoto(!!u.tiene_foto), [u.tiene_foto]);
  useEffect(() => () => { if (elegida?.url) URL.revokeObjectURL(elegida.url); }, [elegida]);

  async function elegir(e) {
    const f = e.target.files?.[0];
    if (archivoRef.current) archivoRef.current.value = '';
    if (!f) return;
    setErr(null); setMsg(null);
    const r = await prepararFoto(f);
    if (r.error) { setErr(r.error); return; }
    setElegida({ blob: r.blob, url: URL.createObjectURL(r.blob), reducida: r.reducida, nombre: f.name });
  }

  function cancelar() { setElegida(null); setErr(null); }

  async function subir() {
    if (!elegida || progreso !== null) return;
    setErr(null); setMsg(null); setProgreso(0);
    try {
      await subirConProgreso('/cuenta/mi/foto', elegida.blob, setProgreso);
      setTieneFoto(true); setSello(Date.now()); setElegida(null);
      setMsg('Foto guardada. Ya se ve arriba, junto a tu nombre.');
      avisarCambioDeFoto(true);
      onCambio();
    } catch (ex) {
      setErr(ex.message || 'No se pudo subir la foto. Vuelve a intentarlo.');
    } finally { setProgreso(null); }
  }

  async function quitar() {
    if (quitando) return;
    setErr(null); setMsg(null); setQuitando(true);
    try {
      await api.post('/cuenta/mi/foto/quitar', {});
      setTieneFoto(false);
      setMsg('Foto quitada. Vuelve a verse tu inicial.');
      avisarCambioDeFoto(false);
      onCambio();
    } catch (ex) { setErr(ex.message || 'No se pudo quitar la foto. Vuelve a intentarlo.'); }
    finally { setQuitando(false); }
  }

  const inicial = (u.nombre_completo || '?').charAt(0).toUpperCase();
  const subiendo = progreso !== null;
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-full bg-gray-100 overflow-hidden flex items-center justify-center text-2xl font-bold text-gray-500 shrink-0 ring-1 ring-gray-200">
          {elegida
            ? <img src={elegida.url} alt="Vista previa de la foto elegida" className="h-full w-full object-cover" />
            : tieneFoto
              ? <img src={`/api/cuenta/foto/mia?v=${sello}`} alt={`Foto de ${u.nombre_completo}`} className="h-full w-full object-cover" onError={() => setTieneFoto(false)} />
              : inicial}
        </div>
        <div className="space-y-2 min-w-0">
          {!elegida && (
            <div className="flex flex-wrap gap-2">
              <label className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer font-medium ${subiendo || quitando ? 'opacity-50 pointer-events-none' : ''}`}>
                <Camera className="h-4 w-4" aria-hidden="true" /> {tieneFoto ? 'Cambiar foto' : 'Elegir foto'}
                <input ref={archivoRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={elegir} className="sr-only" aria-label="Elegir foto" disabled={subiendo || quitando} />
              </label>
              {tieneFoto && (
                <button type="button" onClick={quitar} disabled={quitando} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700 hover:bg-red-50 font-medium disabled:opacity-50">
                  <Trash2 className="h-4 w-4" aria-hidden="true" /> {quitando ? 'Quitando…' : 'Quitar foto'}
                </button>
              )}
            </div>
          )}
          {elegida && !subiendo && (
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={subir} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold">
                <Upload className="h-4 w-4" aria-hidden="true" /> Subir foto
              </button>
              <button type="button" onClick={cancelar} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
                <X className="h-4 w-4" aria-hidden="true" /> Cancelar
              </button>
            </div>
          )}
          {subiendo && (
            <div className="w-48 max-w-full" role="status" aria-live="polite">
              <div className="h-2 rounded bg-gray-200 overflow-hidden"><div className="h-2 bg-[var(--color-marca)] transition-all" style={{ width: `${progreso}%` }} /></div>
              <p className="text-xs text-gray-600 mt-1">Subiendo tu foto… {progreso}%</p>
            </div>
          )}
          <p className="text-xs text-gray-500">
            {elegida
              ? `Así se verá. ${elegida.reducida ? 'Se redujo en tu aparato antes de subirla. ' : ''}Pulsa «Subir foto» para guardarla.`
              : 'JPG, PNG o WebP. Se recorta al centro, se guarda pequeña y sin datos de ubicación, en este servidor. La ven quienes administran tu empresa.'}
          </p>
        </div>
      </div>
      {msg && <Aviso>{msg}</Aviso>}
      {err && <Aviso tipo="error">{err}</Aviso>}
    </div>
  );
}

export function FotoYNombre({ u, reglas, pendientes, onCambio }) {
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [nombre, setNombre] = useState(u.nombre_completo || '');
  const [motivo, setMotivo] = useState('');
  const [editando, setEditando] = useState(false);
  const comoNombre = reglas.nombre_completo.como;
  // Lo que falta para poder enviar, en una frase; null si ya se puede.
  const faltaNombre = nombre.trim().length < 3
    ? 'Escribe el nombre nuevo (al menos tres letras) para poder enviarlo.'
    : (comoNombre === 'solicitud' && motivo.trim().length < 4)
      ? 'Escribe por qué lo cambias (al menos cuatro letras): quien lo firma lo va a leer.'
      : null;

  async function guardarNombre() {
    setErr(null); setMsg(null);
    try {
      const r = await api.post('/cuenta/mi/identidad', { nombre_completo: nombre.trim(), motivo: motivo.trim() });
      setMsg(r.mensaje); setEditando(false); setMotivo(''); onCambio();
    } catch (ex) { setErr(ex.message || 'No se pudo guardar.'); }
  }

  return (
    <Seccion icono={UserRound} titulo="Foto y nombre" descripcion="Tu foto la pones tú. Tu nombre es un dato de identidad.">
      <Foto u={u} onCambio={onCambio} />
      <div className="border-t border-gray-100 pt-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs text-gray-500 uppercase">Nombre completo</div>
            <div className="text-base font-semibold text-gray-900">{u.nombre_completo}</div>
            <p className="text-xs text-gray-500">{COMO[comoNombre]} {reglas.nombre_completo.porque}</p>
          </div>
          {comoNombre !== 'nunca' && !editando && (
            <button type="button" onClick={() => setEditando(true)} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 shrink-0">
              {comoNombre === 'solicitud' ? 'Solicitar cambio' : 'Cambiar'}
            </button>
          )}
        </div>
        {editando && (
          <div className="space-y-2">
            <CampoValidado etiqueta="Nombre nuevo" requerido valor={nombre} validar={(v) => (v.trim().length >= 3 ? null : 'Al menos tres letras.')}>
              {(props) => <input {...props} value={nombre} onChange={(e) => setNombre(e.target.value)} />}
            </CampoValidado>
            {comoNombre === 'solicitud' && (
              <CampoValidado etiqueta="Por qué" requerido valor={motivo} ayuda="Quien lo firma lo va a leer." validar={(v) => (v.trim().length >= 4 ? null : 'Escribe el motivo (mínimo 4 letras).')}>
                {(props) => <input {...props} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Me casé y cambié de apellido" />}
              </CampoValidado>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={guardarNombre} disabled={!!faltaNombre}
                      aria-describedby={faltaNombre ? 'motivo-nombre-nuevo' : undefined}
                      className="px-3 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold disabled:opacity-50">
                {comoNombre === 'solicitud' ? 'Enviar solicitud' : 'Guardar'}
              </button>
              <button type="button" onClick={() => { setEditando(false); setNombre(u.nombre_completo || ''); }} className="px-3 py-2 text-sm rounded-lg border border-gray-200">Cancelar</button>
            </div>
            {/* P13 (29 sep 2026): el censo lo contaba «gris sin decir por qué».
                El botón dice qué falta, pegado a él (Doctrina 2, regla 8). */}
            <Motivo id="motivo-nombre-nuevo" cuando={!!faltaNombre}>{faltaNombre}</Motivo>
          </div>
        )}
        {pendientes.length > 0 && (
          <Aviso tipo="atencion">
            Tienes {pendientes.length} solicitud(es) de cambio pendiente(s): {pendientes.map((p) => p.resumen).join(' · ')}. Mientras se firma, tus datos siguen como estaban.
          </Aviso>
        )}
        {(u.curp !== undefined) && (
          <p className="text-xs text-gray-500">CURP: {u.curp || '—'} · {COMO[reglas.curp.como]} Correo de acceso: {u.email || '—'} · {COMO[reglas.email.como]}</p>
        )}
      </div>
      {msg && <Aviso>{msg}</Aviso>}
      {err && <Aviso tipo="error">{err}</Aviso>}
    </Seccion>
  );
}

function Recuperacion({ u, medios, telegram, onCambio }) {
  const [tel, setTel] = useState(u.telefono_personal || '');
  const [correo, setCorreo] = useState(u.correo_recuperacion || '');
  const [codigo, setCodigo] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [desv, setDesv] = useState(false);
  const [contrasenaDesv, setContrasenaDesv] = useState('');
  const correoMedio = medios.find((m) => m.medio === 'CORREO');
  const faltaVerificar = correoMedio && !correoMedio.disponible && /verificarlo/.test(correoMedio.porque);

  async function guardar() {
    setErr(null); setMsg(null);
    try {
      const r = await api.put('/cuenta/mi/contacto', { telefono_personal: tel, correo_recuperacion: correo });
      setMsg(r.mensaje); onCambio();
    } catch (ex) { setErr(ex.message || 'No se pudo guardar.'); }
  }
  async function verificar() {
    setErr(null); setMsg(null);
    try { const r = await api.post('/cuenta/mi/recuperacion/correo/verificar', { codigo: codigo.trim() }); setMsg(r.mensaje); setCodigo(''); onCambio(); }
    catch (ex) { setErr(ex.message); }
  }
  async function reenviar() {
    setErr(null); setMsg(null);
    try { const r = await api.post('/cuenta/mi/recuperacion/correo/reenviar', {}); setMsg(r.mensaje); }
    catch (ex) { setErr(ex.message); }
  }

  return (
    <Seccion icono={LifeBuoy} titulo="Cómo recuperar mi cuenta" descripcion="Si un día olvidas la contraseña, por dónde te llega una temporal. Solo cuenta lo que está en verde.">
      <ul className="space-y-1.5">
        {medios.map((m) => (
          <li key={m.medio} className="flex items-start gap-2 text-sm">
            <span className={`mt-1 inline-block h-2.5 w-2.5 rounded-full shrink-0 ${m.disponible ? 'bg-green-500' : 'bg-gray-300'}`} aria-hidden="true" />
            <span>
              <span className="font-medium">{ETIQUETA_MEDIO[m.medio] || m.medio}</span>
              {m.valor && <span className="text-gray-600"> · {m.valor}</span>}
              <span className="block text-xs text-gray-500">{m.disponible ? (m.medio === 'ADMINISTRADOR' ? m.porque : 'Disponible.') : m.porque}</span>
            </span>
          </li>
        ))}
      </ul>
      <div className="grid gap-3 sm:grid-cols-2">
        <CampoValidado etiqueta="Teléfono personal" valor={tel} ayuda="Con lada. Sirve cuando vincules Telegram por número.">
          {(props) => <input {...props} value={tel} onChange={(e) => setTel(e.target.value)} inputMode="tel" placeholder="55 1234 5678" />}
        </CampoValidado>
        <CampoValidado etiqueta="Correo personal de recuperación" valor={correo} ayuda="Distinto del de acceso. Se verifica con un código antes de contar.">
          {(props) => <input {...props} value={correo} onChange={(e) => setCorreo(e.target.value)} type="email" placeholder="tu@correo.com" />}
        </CampoValidado>
      </div>
      <div className="flex flex-wrap gap-2 items-end">
        <button type="button" onClick={guardar} className="px-3 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold">Guardar</button>
        {faltaVerificar && (
          <>
            <input value={codigo} onChange={(e) => setCodigo(e.target.value)} inputMode="numeric" maxLength={6} placeholder="Código de 6 dígitos"
                   aria-label="Código de verificación" className="border border-gray-300 rounded-lg px-2 py-2 text-sm w-40" />
            <button type="button" onClick={verificar} disabled={codigo.trim().length !== 6} className="px-3 py-2 text-sm rounded-lg border border-gray-200 disabled:opacity-50">Verificar</button>
            <button type="button" onClick={reenviar} className="text-xs font-semibold text-gray-600">Reenviar código</button>
          </>
        )}
        {telegram && (
          <button type="button" onClick={() => setDesv(true)} className="px-3 py-2 text-sm rounded-lg border border-red-200 text-red-700">Desvincular Telegram</button>
        )}
      </div>
      {msg && <Aviso>{msg}</Aviso>}
      {err && <Aviso tipo="error">{err}</Aviso>}
      {desv && (
        <ModalConfirmar
          titulo="Desvincular mi Telegram"
          descripcion="Deja de servir para recuperar tu contraseña y para recibir credenciales. Escribe tu contraseña para confirmar; después puedes vincularlo otra vez desde el bot."
          extra={<PedirContrasena onValor={setContrasenaDesv} />}
          peligro
          etiquetaConfirmar="Desvincular"
          onConfirmar={async () => { await api.post('/cuenta/mi/telegram/desvincular', { contrasena: contrasenaDesv }); setDesv(false); setContrasenaDesv(''); onCambio(); }}
          onCerrar={() => setDesv(false)}
        />
      )}
    </Seccion>
  );
}

function PedirContrasena({ onValor }) {
  const [v, setV] = useState('');
  return (
    <CampoValidado etiqueta="Tu contraseña" requerido valor={v}>
      {(props) => <CampoContrasena {...props} autoComplete="current-password" value={v} onChange={(e) => { setV(e.target.value); onValor(e.target.value); }} />}
    </CampoValidado>
  );
}

function Aspecto({ u, reglas, onCambio }) {
  const [msg, setMsg] = useState(null);
  async function fijar(valor) {
    elegir(valor);
    try { await api.put('/cuenta/mi/preferencias', { aspecto: valor }); setMsg('Guardado. Se aplica en todos tus aparatos al entrar.'); onCambio(); }
    catch (ex) { setMsg(ex.message); }
  }
  return (
    <Seccion icono={Palette} titulo="Aspecto e idioma" descripcion={reglas.aspecto.porque}>
      <div role="radiogroup" aria-label="Aspecto" className="flex gap-1 bg-gray-100 rounded-lg p-0.5 w-fit">
        {[['sistema', 'Como el sistema'], ['claro', 'Claro'], ['oscuro', 'Oscuro']].map(([valor, etiqueta]) => (
          <button key={valor} type="button" role="radio" aria-checked={u.aspecto === valor} onClick={() => fijar(valor)}
                  className={`px-3 py-1.5 text-sm rounded-md ${u.aspecto === valor ? 'bg-white shadow font-semibold' : 'text-gray-600'}`}>
            {etiqueta}
          </button>
        ))}
      </div>
      <p className="text-xs text-gray-500">Idioma: español. {reglas.idioma.porque}</p>
      {msg && <Aviso>{msg}</Aviso>}
    </Seccion>
  );
}

// =====================================================================
// MIS DATOS · derechos ARCO con folio (TODO-SIRVE F2, 30 sep 2026)
// =====================================================================
// Los cuatro derechos, no dos. Cada solicitud recibe su FOLIO y su FECHA
// LÍMITE (20 días hábiles, dato de `arco_plazo.json`), que se enseñan en
// cuanto se envía y se quedan en «Mis solicitudes».
const DERECHOS = {
  ACCESO: {
    boton: 'Solicitar una copia',
    titulo: 'Solicitar una copia de mis datos',
    descripcion: 'Quien administra tu empresa te entrega todo lo que TEGO guarda de ti. «Ver mis datos» ya te enseña lo principal al momento.',
    motivo: false,
  },
  RECTIFICACION: {
    boton: 'Solicitar corrección',
    titulo: 'Solicitar una corrección',
    descripcion: 'Escribe qué dato está mal y cómo debe quedar. Quien administra lo resuelve y queda constancia.',
    motivo: true,
  },
  CANCELACION: {
    boton: 'Solicitar borrado',
    titulo: 'Solicitar que se borren mis datos',
    descripcion: 'Quien administra tu empresa recibe la solicitud y la resuelve con acta. Borrar tus datos deja tu cuenta sin acceso.',
    motivo: true,
    peligro: true,
  },
  OPOSICION: {
    boton: 'Oponerme a un uso',
    titulo: 'Oponerme a un uso de mis datos',
    descripcion: 'Escribe qué uso de tus datos no quieres. Quien administra lo revisa y te contesta por escrito.',
    motivo: true,
  },
};
const NOMBRE_DERECHO = {
  ACCESO: 'Copia de mis datos', RECTIFICACION: 'Corrección', CANCELACION: 'Borrado', OPOSICION: 'Oposición a un uso',
};
const ESTADO_SOLICITUD = {
  PENDIENTE: ['Esperando respuesta', 'text-amber-900 bg-amber-50 border-amber-200'],
  ATENDIDA: ['Atendida', 'text-green-800 bg-green-50 border-green-200'],
  RECHAZADA: ['Rechazada', 'text-red-700 bg-red-50 border-red-200'],
};

function MisDatos() {
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [pedir, setPedir] = useState(null);
  const [json, setJson] = useState(null);
  const [mias, setMias] = useState(null);
  const [errMias, setErrMias] = useState(null);

  const cargarMias = useCallback(() => {
    api.get('/arco/mis-solicitudes')
      .then((l) => { setMias(l); setErrMias(null); })
      .catch((e) => setErrMias(e.message || 'No se pudo cargar tu lista de solicitudes. Revisa la conexión y vuelve a intentarlo.'));
  }, []);
  useEffect(cargarMias, [cargarMias]);

  const [bajando, setBajando] = useState(null);
  async function descargar() {
    setErr(null);
    try { const d = await api.get('/cuenta/mi/datos'); setJson(JSON.stringify(d, null, 2)); }
    catch (ex) { setErr(ex.message); }
  }
  // TODO-SIRVE F2-B: el archivo, no solo el texto en pantalla. El JSON sale
  // del mismo `GET /cuenta/mi/datos` (todo el censo de datos personales);
  // el PDF, de `GET /cuenta/mi/datos.pdf`.
  async function bajarArchivo(tipo) {
    setErr(null); setMsg(null); setBajando(tipo);
    try {
      if (tipo === 'pdf') {
        guardarArchivo(await api.bajar('/cuenta/mi/datos.pdf'), 'mis-datos-tego.pdf');
      } else {
        const d = await api.get('/cuenta/mi/datos');
        guardarArchivo(new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' }), 'mis-datos-tego.json');
      }
      setMsg(tipo === 'pdf' ? 'Listo: se descargó «mis-datos-tego.pdf».' : 'Listo: se descargó «mis-datos-tego.json».');
    } catch (ex) {
      setErr(`${ex.message || 'No se descargó el archivo.'} Revisa la conexión y vuelve a intentarlo.`);
    } finally { setBajando(null); }
  }
  const d = pedir ? DERECHOS[pedir] : null;
  return (
    <Seccion icono={FileDown} titulo="Mis datos" descripcion="Lo que TEGO guarda de ti, para verlo, corregirlo, pedir que se borre u oponerte a un uso (derechos ARCO).">
      <Ayuda id="cuenta.arco.pantalla" />
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={descargar} className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">Ver mis datos</button>
        <button type="button" onClick={() => bajarArchivo('json')} disabled={!!bajando}
                className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
          {bajando === 'json' ? 'Preparando…' : 'Descargar mis datos (archivo)'}
        </button>
        <button type="button" onClick={() => bajarArchivo('pdf')} disabled={!!bajando}
                className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
          {bajando === 'pdf' ? 'Preparando…' : 'Descargar en PDF'}
        </button>
        {Object.entries(DERECHOS).map(([tipo, x]) => (
          <button key={tipo} type="button" onClick={() => setPedir(tipo)}
                  className={`min-h-[44px] px-3 py-2 text-sm rounded-lg border ${x.peligro ? 'border-red-200 text-red-700' : 'border-gray-200 hover:bg-gray-50'}`}>
            {x.boton}
          </button>
        ))}
      </div>
      <Ayuda id="cuenta.mis_datos.descarga" />
      {json && (
        <div className="space-y-1">
          <p className="text-xs text-gray-500">Selecciona y copia; es un archivo de texto con tus datos.</p>
          <pre className="text-xs bg-gray-50 border border-gray-200 rounded-lg p-2 overflow-x-auto max-h-64 select-all">{json}</pre>
        </div>
      )}
      {msg && <Aviso>{msg}</Aviso>}
      {err && <Aviso tipo="error">{err}</Aviso>}

      <div className="border-t border-gray-100 pt-3 space-y-2">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <FileText className="h-4 w-4 text-gray-500" aria-hidden="true" /> Mis solicitudes
        </h3>
        {errMias && <Aviso tipo="error">{errMias}</Aviso>}
        {mias === null && !errMias && <p className="text-sm text-gray-500" role="status">Cargando tus solicitudes…</p>}
        {mias !== null && mias.length === 0 && (
          <p className="text-sm text-gray-600">
            Sin solicitudes enviadas. Aquí aparece cada solicitud que envíes con los botones de arriba, con su folio,
            su fecha límite y la respuesta.
          </p>
        )}
        {mias !== null && mias.length > 0 && (
          <ul className="space-y-2">
            {mias.map((m) => {
              const [etiqueta, clases] = ESTADO_SOLICITUD[m.estado] || [m.estado, 'text-gray-700 bg-gray-50 border-gray-200'];
              return (
                <li key={m.id} className="border border-gray-200 rounded-lg p-2.5 text-sm space-y-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono font-semibold text-gray-900">{m.folio}</span>
                    <span className="text-gray-700">{NOMBRE_DERECHO[m.tipo] || m.tipo}</span>
                    <span className={`text-xs border rounded px-1.5 py-0.5 ${clases}`}>{etiqueta}</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    Enviada el {fechaCorta(m.creada_en)} · respuesta a más tardar el {fechaCorta(m.responder_antes_de)}
                  </div>
                  {m.respuesta && <div className="text-xs text-gray-700">Respuesta: {m.respuesta}</div>}
                </li>
              );
            })}
          </ul>
        )}
        <Ayuda id="cuenta.arco.plazo" />
      </div>

      {pedir && (
        <ModalConfirmar
          titulo={d.titulo}
          descripcion={d.descripcion}
          pedirMotivo={d.motivo}
          peligro={!!d.peligro}
          etiquetaConfirmar="Enviar solicitud"
          onConfirmar={async (motivo) => {
            const r = await api.post('/arco/solicitudes', { tipo: pedir, detalle: motivo || null });
            setPedir(null);
            setMsg(`Solicitud enviada con folio ${r.folio}. Quien administra tu empresa debe contestar a más tardar el ${fechaCorta(r.responder_antes_de)}; la verás en «Mis solicitudes».`);
            cargarMias();
          }}
          onCerrar={() => setPedir(null)}
        />
      )}
    </Seccion>
  );
}


// =====================================================================
// CORREO DE ACCESO · cambiarlo con código al correo nuevo (TODO-SIRVE F2)
// =====================================================================
// Hasta que se teclea el código que llega al correo NUEVO, se entra con el
// de antes: un error de dedo ya no deja a nadie fuera. Si el cambio pide
// firma, la firma llega con el correo ya confirmado.
const CORREO_VALIDO = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function horaCorta(iso) {
  try { return new Intl.DateTimeFormat('es-MX', { hour: '2-digit', minute: '2-digit' }).format(new Date(iso)); }
  catch { return ''; }
}

export function CorreoDeAcceso({ u, regla, pendiente, onCambio }) {
  const [correo, setCorreo] = useState('');
  const [codigo, setCodigo] = useState('');
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);
  const [ocupado, setOcupado] = useState(false);
  const [editando, setEditando] = useState(false);

  const limpio = correo.trim().toLowerCase();
  const actual = (u.email || '').toLowerCase();
  const faltaCorreo = !limpio
    ? 'Escribe el correo nuevo para poder mandar el código.'
    : !CORREO_VALIDO.test(limpio)
      ? 'Ese correo no parece válido: revisa que lleve @ y un dominio, sin espacios.'
      : limpio === actual ? 'Ése ya es tu correo de acceso: escribe uno distinto.' : null;
  const cifras = codigo.replace(/\D/g, '');
  const faltaCodigo = cifras.length !== 6 ? 'Escribe los seis dígitos que llegaron al correo nuevo.' : null;

  async function correr(fn) {
    setErr(null); setMsg(null); setOcupado(true);
    try { await fn(); } catch (ex) { setErr(ex.message || 'No se pudo completar. Revisa la conexión y vuelve a intentarlo.'); }
    finally { setOcupado(false); }
  }
  const mandar = (a) => correr(async () => {
    const r = await api.post('/cuenta/mi/correo-acceso', { correo: a });
    setMsg(r.mensaje); setCorreo(''); setEditando(false); onCambio();
  });
  const confirmar = () => correr(async () => {
    const r = await api.post('/cuenta/mi/correo-acceso/verificar', { codigo: cifras });
    setMsg(r.mensaje); setCodigo(''); onCambio();
  });
  const cancelar = () => correr(async () => {
    const r = await api.post('/cuenta/mi/correo-acceso/cancelar', {});
    setMsg(r.mensaje); setCodigo(''); onCambio();
  });

  return (
    <Seccion icono={MailCheck} titulo="Correo de acceso" descripcion="El correo con el que entras a TEGO. Para cambiarlo, confirmas el nuevo con un código.">
      <p className="text-sm"><span className="text-gray-500">Entras con:</span> <span className="font-semibold text-gray-900 break-all">{u.email || '—'}</span></p>
      {regla?.como === 'solicitud' && !pendiente && (
        <p className="text-xs text-gray-500">Después de confirmar el código, el cambio lo firma quien administra tu empresa.</p>
      )}

      {pendiente ? (
        <div className="space-y-2">
          <Aviso tipo="atencion">
            Mandamos un código a <strong className="break-all">{pendiente.correo_nuevo}</strong>. Vale hasta las {horaCorta(pendiente.expira_en)}. Mientras no lo escribas, entras con el correo de antes.
          </Aviso>
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">Código</span>
            <input value={codigo} onChange={(e) => setCodigo(e.target.value.replace(/\D/g, '').slice(0, 6))}
                   inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="6 dígitos"
                   aria-label="Código de verificación del correo nuevo" className="entrada w-40 text-lg tracking-widest" />
          </label>
          <Ayuda id="cuenta.correo_acceso.codigo" />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={confirmar} disabled={!!faltaCodigo || ocupado}
                    aria-describedby={faltaCodigo ? 'motivo-codigo-acceso' : undefined}
                    className="min-h-[44px] px-4 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold disabled:opacity-50">
              {ocupado ? 'Confirmando…' : 'Confirmar'}
            </button>
            <button type="button" onClick={() => mandar(pendiente.correo_nuevo)} disabled={ocupado}
                    className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
              Mandar otro código
            </button>
            <button type="button" onClick={cancelar} disabled={ocupado}
                    className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
              Cancelar el cambio
            </button>
          </div>
          <Motivo id="motivo-codigo-acceso" cuando={!!faltaCodigo}>{faltaCodigo}</Motivo>
        </div>
      ) : editando ? (
        <div className="space-y-2">
          <CampoValidado etiqueta="Correo nuevo" requerido valor={correo}
                         validar={() => (limpio && faltaCorreo ? faltaCorreo : null)}>
            {(props) => <input {...props} value={correo} onChange={(e) => setCorreo(e.target.value.replace(/\s/g, ''))}
                               type="email" inputMode="email" autoComplete="email" placeholder="tu@correo.com" />}
          </CampoValidado>
          <Ayuda id="cuenta.correo_acceso.campo" />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => mandar(limpio)} disabled={!!faltaCorreo || ocupado}
                    aria-describedby={faltaCorreo ? 'motivo-correo-acceso' : undefined}
                    className="min-h-[44px] px-4 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold disabled:opacity-50">
              {ocupado ? 'Mandando…' : 'Mandar código'}
            </button>
            <button type="button" onClick={() => { setEditando(false); setCorreo(''); }}
                    className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200">Cancelar</button>
          </div>
          <Motivo id="motivo-correo-acceso" cuando={!!faltaCorreo}>{faltaCorreo}</Motivo>
        </div>
      ) : (
        <button type="button" onClick={() => { setEditando(true); setMsg(null); setErr(null); }}
                className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">
          Cambiar mi correo de acceso
        </button>
      )}
      {msg && <Aviso>{msg}</Aviso>}
      {err && <Aviso tipo="error">{err}</Aviso>}
    </Seccion>
  );
}


function MisPermisosConNombre({ codigos }) {
  const [permisos, setPermisos] = useState(null);
  useEffect(() => {
    let vigente = true;
    api.get('/experiencia/mis-permisos')
      .then((r) => vigente && setPermisos(r.permisos || []))
      .catch(() => vigente && setPermisos(null));
    return () => { vigente = false; };
  }, [codigos]);
  if (!codigos || codigos.length === 0) {
    return <p className="text-sm text-gray-600">Tu rol no trae permisos sueltos: lo que puedes hacer viene de tu puesto.</p>;
  }
  if (permisos === null) {
    return <ul className="flex flex-wrap gap-1">{codigos.map((p) => <li key={p} className="text-xs bg-gray-100 rounded px-2 py-0.5">{p.replace(/[._]/g, ' ')}</li>)}</ul>;
  }
  const grupos = new Map();
  for (const p of permisos) { if (!grupos.has(p.grupo)) grupos.set(p.grupo, []); grupos.get(p.grupo).push(p.nombre); }
  return (
    <dl className="text-sm">
      {[...grupos.entries()].map(([g, nombres]) => (
        <div key={g} className="flex flex-wrap gap-x-2 py-0.5">
          <dt className="font-medium text-gray-800">{g}:</dt>
          <dd className="text-gray-700">{nombres.join(', ')}</dd>
        </div>
      ))}
    </dl>
  );
}


// =====================================================================
// MI ROL Y PERMISOS · agrupados, con plegado, y «Pedir un permiso»
// (TODO-SIRVE F2-B, 30 sep 2026)
// =====================================================================
// Antes: una línea por grupo con los nombres pegados con comas, y ninguna
// forma de pedir lo que falta. Ahora cada permiso sale del catálogo con su
// descripción, agrupado por módulo y plegado (en el teléfono, una lista de
// cuarenta permisos abiertos es una pared), y «Pedir un permiso» deja el
// pedido y avisa a quien lo puede conceder.
const ESTADO_PEDIDO = {
  ESPERANDO: ['Esperando', 'text-amber-900 bg-amber-50 border-amber-200'],
  CONCEDIDO: ['Concedido', 'text-green-800 bg-green-50 border-green-200'],
};

export function MisPermisos({ codigos }) {
  const [d, setD] = useState(null);
  const [fallo, setFallo] = useState(false);
  const [version, setVersion] = useState(0);
  const recargar = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    let vivo = true;
    api.get('/cuenta/mi/permisos')
      // Una respuesta sin la forma esperada (un servidor anterior que
      // contesta otra cosa) cuenta como fallo: se enseña la lista de antes.
      .then((r) => { if (vivo) { const ok = Array.isArray(r?.grupos); setD(ok ? r : null); setFallo(!ok); } })
      .catch(() => { if (vivo) setFallo(true); });
    return () => { vivo = false; };
  }, [version]);

  if (fallo) return <MisPermisosConNombre codigos={codigos} />;
  if (!d) return <p role="status" className="text-sm text-gray-500">Cargando tus permisos…</p>;
  return (
    <div className="space-y-3">
      {d.total === 0 ? (
        <p className="text-sm text-gray-600">Tu rol no trae permisos sueltos: lo que puedes hacer viene de tu puesto.</p>
      ) : (
        <>
          <p className="text-sm text-gray-700">{d.total === 1 ? 'Tienes 1 permiso' : `Tienes ${d.total} permisos`}, por módulo. Toca un módulo para ver cuáles.</p>
          <div className="space-y-1.5">
            {d.grupos.map((g) => (
              <details key={g.grupo} className="group border border-gray-200 rounded-lg">
                <summary className="min-h-[44px] flex items-center gap-2 px-3 py-2 cursor-pointer select-none list-none">
                  <ChevronRight className="h-4 w-4 text-gray-400 transition-transform group-open:rotate-90" aria-hidden="true" />
                  <span className="font-medium text-gray-900">{g.grupo}</span>
                  <span className="ml-auto text-xs text-gray-500">{g.permisos.length}</span>
                </summary>
                <ul className="px-3 pb-2 space-y-1.5">
                  {g.permisos.map((p) => (
                    <li key={p.codigo} className="text-sm">
                      <span className="text-gray-900">{p.nombre}</span>
                      {p.origen === 'APARTE' && (
                        <span className="ml-2 text-xs border rounded px-1.5 py-0.5 text-sky-800 bg-sky-50 border-sky-200">concedido aparte</span>
                      )}
                      {p.descripcion && <span className="block text-xs text-gray-500">{p.descripcion}</span>}
                    </li>
                  ))}
                </ul>
              </details>
            ))}
          </div>
        </>
      )}
      <PedirUnPermiso pedibles={d.se_pueden_pedir || []} pedidos={d.pedidos || []} alPedir={recargar} />
    </div>
  );
}

function PedirUnPermiso({ pedibles, pedidos, alPedir }) {
  const [abierto, setAbierto] = useState(false);
  const [codigo, setCodigo] = useState('');
  const [motivo, setMotivo] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const esperando = new Set(pedidos.filter((p) => p.estado === 'ESPERANDO').map((p) => p.permiso));
  const sinNada = pedibles.length === 0;
  const faltaElegir = !codigo ? 'Elige qué permiso necesitas.' : null;
  const grupos = new Map();
  for (const p of pedibles) { if (!grupos.has(p.grupo)) grupos.set(p.grupo, []); grupos.get(p.grupo).push(p); }
  const elegido = pedibles.find((p) => p.codigo === codigo);

  async function enviar() {
    setErr(null); setMsg(null); setOcupado(true);
    try {
      const r = await api.post('/cuenta/mi/permisos/pedir', { permiso: codigo, motivo: motivo.trim() || null });
      setMsg(r.mensaje); setAbierto(false); setCodigo(''); setMotivo(''); alPedir?.();
    } catch (ex) {
      setErr(ex.message || 'No se envió el pedido. Revisa la conexión y vuelve a intentarlo.');
    } finally { setOcupado(false); }
  }

  return (
    <div className="border-t border-gray-100 pt-3 space-y-2">
      {!abierto && (
        <>
          <button type="button" onClick={() => { setAbierto(true); setMsg(null); setErr(null); }} disabled={sinNada}
                  aria-describedby={sinNada ? 'motivo-pedir-permiso' : undefined}
                  className="min-h-[44px] inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
            <Hand className="h-4 w-4" aria-hidden="true" /> Pedir un permiso
          </button>
          <Motivo id="motivo-pedir-permiso" cuando={sinNada}>
            Ya tienes todos los permisos que se conceden por separado. Para algo más, habla con quien administra sobre un cambio de puesto.
          </Motivo>
        </>
      )}
      {abierto && (
        <div className="space-y-2">
          <label className="block">
            <span className="block text-sm font-semibold text-gray-700 mb-1">¿Qué permiso necesitas? <span className="text-red-600">*</span></span>
            <select value={codigo} onChange={(e) => setCodigo(e.target.value)} className="entrada w-full min-h-[44px]">
              <option value="">Elige uno…</option>
              {[...grupos.entries()].map(([g, ps]) => (
                <optgroup key={g} label={g}>
                  {ps.map((p) => (
                    <option key={p.codigo} value={p.codigo}>{p.nombre}{esperando.has(p.codigo) ? ' (ya pedido)' : ''}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
          {elegido?.descripcion && <p className="text-xs text-gray-600">{elegido.descripcion}</p>}
          <CampoValidado etiqueta="¿Para qué lo necesitas?" valor={motivo} ayuda="Opcional. Ayuda a quien administra a decidir más rápido.">
            {(props) => <input {...props} value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} placeholder="Por ejemplo: para cerrar el inventario del mes" />}
          </CampoValidado>
          <Ayuda id="cuenta.permisos.pedir" />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={enviar} disabled={!!faltaElegir || ocupado}
                    aria-describedby={faltaElegir ? 'motivo-enviar-pedido' : undefined}
                    className="min-h-[44px] px-4 py-2 text-sm rounded-lg bg-[var(--color-marca)] text-white font-semibold disabled:opacity-50">
              {ocupado ? 'Enviando…' : 'Enviar pedido'}
            </button>
            <button type="button" onClick={() => { setAbierto(false); setCodigo(''); setMotivo(''); }}
                    className="min-h-[44px] px-3 py-2 text-sm rounded-lg border border-gray-200">Cancelar</button>
          </div>
          <Motivo id="motivo-enviar-pedido" cuando={!!faltaElegir}>{faltaElegir}</Motivo>
        </div>
      )}
      {msg && <Aviso>{msg}</Aviso>}
      {err && <Aviso tipo="error">{err}</Aviso>}
      {pedidos.length > 0 && (
        <div className="space-y-1">
          <h3 className="text-sm font-semibold text-gray-900">Mis pedidos</h3>
          <ul className="space-y-1">
            {pedidos.map((p) => {
              const [etiqueta, clases] = ESTADO_PEDIDO[p.estado] || [p.estado, 'text-gray-700 bg-gray-50 border-gray-200'];
              return (
                <li key={p.id} className="text-sm flex flex-wrap items-center gap-2">
                  <span className="text-gray-900">{p.nombre}</span>
                  <span className={`text-xs border rounded px-1.5 py-0.5 ${clases}`}>{etiqueta}</span>
                  <span className="text-xs text-gray-500">pedido el {fechaCorta(p.pedido_en)}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
